import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import SaveIcon from "@mui/icons-material/SaveOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import { EditDetailsDialog } from "@/components/common/edit-details-dialog";
import { EntityActionsMenu } from "@/components/common/entity-actions-menu";
import { WorkflowStepEditor } from "@/components/workflows/step-editor";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useUndoableDelete } from "@/lib/undoable-delete";
import { useLiveInvalidate } from "@/lib/live";
import {
  type WorkflowInstance,
  type WorkflowStep,
  parseSteps,
  useWorkflow,
  useWorkflowInstances,
  useWorkflowMutations,
  workflowQuery,
} from "@/api/workflows";

export const Route = createFileRoute("/_app/workflows/$workflowId")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(workflowQuery(params.workflowId)),
  component: WorkflowDetail,
});

function WorkflowDetail() {
  const { workflowId } = Route.useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const undoableDelete = useUndoableDelete<{ id: string; name: string }>({
    noun: "Workflow",
    key: (i) => i.id,
    name: (i) => i.name,
    mutate: (i) => m.remove.mutateAsync(i.id),
    invalidate: [["workflows"]],
  });
  const wf = useWorkflow(workflowId);
  const instances = useWorkflowInstances(workflowId);
  const m = useWorkflowMutations(workflowId);
  useLiveInvalidate([["workflows", "instances", workflowId]]);

  const [steps, setSteps] = useState<WorkflowStep[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => {
    if (wf.data) {
      setSteps(parseSteps(wf.data.steps));
      setDirty(false);
    }
  }, [wf.data]);

  if (wf.isLoading) return <LoadingState />;
  if (wf.error || !wf.data)
    return <ErrorState error={wf.error ?? "Not found"} onRetry={() => wf.refetch()} />;

  const instanceColumns: Column<WorkflowInstance>[] = [
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    { id: "step", header: "Step", align: "right", cell: (r) => (r.currentStepIndex ?? 0) + 1 },
    { id: "by", header: "Triggered by", cell: (r) => r.triggeredBy ?? "—", nowrap: true },
    { id: "started", header: "Started", cell: (r) => <RelativeTime value={r.startedAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) =>
        ["RUNNING", "WAITING_APPROVAL", "AWAITING_APPROVAL"].includes(r.status.toUpperCase()) ? (
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
            <Button size="small" onClick={() => m.approve.mutate(r.id)}>
              Approve
            </Button>
            <Button size="small" color="error" onClick={() => m.cancel.mutate(r.id)}>
              Cancel
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <PageContainer maxWidth={860}>
      <PageHeader
        title={wf.data.name}
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: "Workflows", to: "/workflows" }, { label: wf.data.name }]}
          />
        }
        description={wf.data.description ?? undefined}
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={async () => {
                await m.trigger.mutateAsync(workflowId);
                toast.success("Workflow triggered");
              }}
            >
              Run
            </Button>
            <EntityActionsMenu
              items={[
                {
                  label: "Edit details",
                  icon: <DriveFileRenameOutlineIcon fontSize="small" />,
                  onClick: () => setEditOpen(true),
                },
                {
                  label: "Duplicate",
                  icon: <ContentCopyIcon fontSize="small" />,
                  onClick: async () => {
                    const dup = await m.duplicate.mutateAsync(workflowId);
                    toast.success("Workflow duplicated");
                    navigate({ to: "/workflows/$workflowId", params: { workflowId: dup.id } });
                  },
                },
                {
                  label: wf.data.archivedAt ? "Unarchive" : "Archive",
                  icon: <ArchiveIcon fontSize="small" />,
                  divider: true,
                  onClick: async () => {
                    await m.setArchived.mutateAsync({
                      wid: workflowId,
                      archived: !wf.data!.archivedAt,
                    });
                    toast.success(wf.data!.archivedAt ? "Unarchived" : "Archived");
                  },
                },
                {
                  label: "Delete",
                  icon: <DeleteIcon fontSize="small" />,
                  danger: true,
                  onClick: () => {
                    undoableDelete.remove(
                      { id: workflowId, name: wf.data!.name },
                      {
                        onUndo: () =>
                          navigate({ to: "/workflows/$workflowId", params: { workflowId } }),
                      },
                    );
                    navigate({ to: "/workflows" });
                  },
                },
              ]}
            />
          </>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
        <StatusChip status={wf.data.status ?? "DRAFT"} />
      </Stack>

      <Stack
        direction="row"
        sx={{ mb: 1.5, alignItems: "center", justifyContent: "space-between" }}
      >
        <Typography variant="h3">Steps</Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!dirty || m.update.isPending}
          onClick={async () => {
            await m.update.mutateAsync({ wid: workflowId, body: { steps } });
            setDirty(false);
            toast.success("Steps saved");
          }}
        >
          Save
        </Button>
      </Stack>
      <Card sx={{ p: 2, mb: 3 }}>
        <WorkflowStepEditor
          value={steps}
          onChange={(next) => {
            setSteps(next);
            setDirty(true);
          }}
        />
      </Card>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Instances
      </Typography>
      <Card>
        <DataTable
          columns={instanceColumns}
          data={instances.data?.content}
          isLoading={instances.isLoading}
          error={instances.error}
          onRetry={() => instances.refetch()}
          rowKey={(r) => r.id}
          empty={<span>No runs yet.</span>}
        />
      </Card>

      <EditDetailsDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        noun="workflow"
        initial={{ name: wf.data.name, description: wf.data.description ?? "" }}
        onSave={(v) =>
          m.update.mutateAsync({
            wid: workflowId,
            body: { name: v.name, description: v.description },
          })
        }
      />
    </PageContainer>
  );
}
